import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TypeAhead } from '../sync/PriceLookup';
import { searchPricingParts, resolveCustomerPrice } from '../../services/pricingApi';
import { listInventoryWarehouses } from '../../services/inventoryApi';
import { useSession } from '../auth/SessionProvider';
import { PageHeader, Button, Badge, Card } from '../ui';
import {
  closeOrder,
  deleteOrder,
  getCustomerDetail,
  getLiveCredit,
  getReservation,
  getTestOrderConfig,
  listCountries,
  listShipVia,
  listSubmissions,
  quoteOrder,
  readOrderBack,
  reconcileOrders,
  reopenOrder,
  submitOrder,
  voidOrder,
} from '../../services/testOrdersApi';
import './test.css';
import './checkout.css';

/*
 * The checkout.
 *
 * Two things at once, and the split is deliberate and visible on the page.
 *
 * Above the fold it is the storefront checkout as designed: the order as a
 * customer would place it, with the delivery or collection choice, the real
 * ready-to-ship and backorder split, real freight from the postcode zone
 * table, and the real totals. Nothing on it is mocked. The split comes from
 * POST /orders/quote reading staged availability, the freight rate is the one
 * the B2B storefront publishes for that address, and the credit figure is a
 * live read against Epicor.
 *
 * Below it is the console the page has always been: the write-gate posture,
 * every order this console has created, the four verification questions from
 * documents/Epicor_Order_Posting.md section 7, and the cleanup controls. That
 * machinery is why the page exists -- scripts/verify_epicor_order_create.py
 * refuses to run against a production instance, and saas525 is the only one
 * configured -- so it is kept whole rather than trimmed to suit the design.
 *
 * Question 2 is still the disqualifying one: if the correlation stamp does not
 * come back on a read, the recovery mechanism is inoperative and the push must
 * not be enabled.
 *
 * The one thing in the design that is not built is the coupon field. There is
 * no discount engine anywhere in this system, so the field could only ever
 * have been decorative, and a checkout that accepts a coupon code and silently
 * ignores it is worse than one that does not offer the box.
 */

const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;
const QUOTE_DEBOUNCE_MS = 400;
const NOTES_MAX = 200;

const TERMINAL = ['submitted', 'failed', 'needs_review', 'abandoned'];

const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
};

const money = (n) => (Number.isFinite(Number(n)) ? `$${fmtMoney(n)}` : '—');

// Quantities arrive from the availability endpoint as floats; trim the noise.
const fmtQty = (n) => {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : '—';
};

const errText = (err) =>
  err?.response?.data?.detail || err?.message || String(err);

const newExternalRef = () =>
  `verify-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const emptyLine = () => ({
  key: Math.random().toString(36).slice(2),
  part: null,
  qty: '1',
  price: null,
  priceError: null,
  pricing: false,
  warehouse: null,
});

// Full names, not abbreviations: the incumbent writes 'New South Wales' to
// OTSState, and parity with it is the standing goal (probe_order_payload.md).
const AU_STATES = [
  'Australian Capital Territory',
  'New South Wales',
  'Northern Territory',
  'Queensland',
  'South Australia',
  'Tasmania',
  'Victoria',
  'Western Australia',
];

const emptyOts = () => ({
  first_name: '',
  last_name: '',
  address1: '',
  address2: '',
  suburb: '',
  state: '',
  postcode: '',
  country_num: '',
});

// The one-time address as a single line, for the submission card.
const formatOts = (o) =>
  [o.address1, o.address2, o.suburb, o.state, o.postcode]
    .filter(Boolean)
    .join(', ');

// A ship-to or warehouse row as a single address line.
const addressLine = (r) =>
  [r?.address1 ?? r?.address_line1, r?.address2 ?? r?.address_line2, r?.city, r?.state, r?.zip ?? r?.postcode]
    .filter(Boolean)
    .join(', ');

// The stored request, tolerant of arriving as JSON text rather than an object:
// it is written to a jsonb column but round-trips through the ledger, and a
// string here would silently read as "nothing was requested".
const requestedPayload = (submission) => {
  const raw = submission?.request_payload;
  if (!raw) return {};
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

/* ── Icons ──
   Inline rather than an icon package: five glyphs do not justify a dependency,
   and these inherit currentColor so they follow the token that colours the
   text beside them. */
const Icon = ({ d, size = 18, fill = false }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d={d} />
  </svg>
);

const IconPin = (p) => <Icon {...p} d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Zm0-8.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />;
const IconStore = (p) => <Icon {...p} d="M4 10v9h16v-9M3 4h18l1 5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0l1-5Z" />;
const IconTruck = (p) => <Icon {...p} d="M3 16V6h11v10M14 9h4l3 3v4h-7M7.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm10 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />;
const IconLock = (p) => <Icon {...p} d="M6 11h12v9H6v-9Zm3 0V7a3 3 0 0 1 6 0v4" />;
const IconCheck = (p) => <Icon {...p} d="m5 13 4 4L19 7" />;
const IconClock = (p) => <Icon {...p} d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-14v5l3 2" />;
const IconAlert = (p) => <Icon {...p} d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 8v5m0 3h.01" />;
const IconBox = (p) => <Icon {...p} d="M3 8.5 12 4l9 4.5v7L12 20l-9-4.5v-7Zm0 0 9 4.5m0 0 9-4.5m-9 4.5V20" />;
const IconCart = (p) => <Icon {...p} d="M3 4h2l2.4 11.3a1 1 0 0 0 1 .7h8.2a1 1 0 0 0 1-.8L19 8H6M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />;
const IconShield = (p) => <Icon {...p} d="M12 3 5 6v5.5c0 4 3 7.7 7 9.5 4-1.8 7-5.5 7-9.5V6l-7-3Z" />;
const IconChevron = ({ open }) => (
  <Icon size={16} d={open ? 'm6 15 6-6 6 6' : 'm9 6 6 6-6 6'} />
);

const TestCheckout = () => {
  const { isAdmin, loading: sessionLoading } = useSession();

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  /* ── Operator setup: what stands in for a cart ── */
  const [setupOpen, setSetupOpen] = useState(true);
  const [custKey, setCustKey] = useState('');
  const [custDetail, setCustDetail] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);
  // The fulfil warehouse is an order-level bulk default: Epicor holds the
  // warehouse per line (and derives the release plant from it), so picking one
  // here stamps it onto every line.
  const [warehouses, setWarehouses] = useState([]);
  const [fulfilWarehouse, setFulfilWarehouse] = useState('');

  /* ── Section 1: delivery ── */
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [shipToNum, setShipToNum] = useState('');
  const [shipVia, setShipVia] = useState([]);
  const [shipViaCode, setShipViaCode] = useState('');
  const [freightService, setFreightService] = useState('standard');
  const [pickupWarehouse, setPickupWarehouse] = useState('');
  const [authorityToLeave, setAuthorityToLeave] = useState(false);
  const [shipComplete, setShipComplete] = useState(false);
  // Delivery address source: a saved ship-to, or a free-form one-time address
  // posted to Epicor as UseOTS.
  const [addressMode, setAddressMode] = useState('saved');
  const [ots, setOts] = useState(emptyOts());
  const [countries, setCountries] = useState([]);

  /* ── Section 2: order details ── */
  const [poNum, setPoNum] = useState('');
  const [orderComment, setOrderComment] = useState('');
  const [email, setEmail] = useState('');

  /* ── Section 3: payment ── */
  const [paymentMethod, setPaymentMethod] = useState('on_account');
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvc: '' });
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Set only by a rejected Place Order, so the danger border and the alert
  // appear in response to an attempt rather than pre-emptively scolding
  // somebody who has not finished filling the form in.
  const [attempted, setAttempted] = useState(false);

  /* ── The quote ── */
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [readback, setReadback] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  // The quote as it stood when the order was placed, kept so the placed screen
  // shows the figures the buyer agreed to rather than a re-quote taken after
  // stock moved.
  const [placedQuote, setPlacedQuote] = useState(null);
  // The on-account position for the picked customer, and the hold this
  // submission has placed on it. Surfaced because the order is gated on both:
  // the checkout reserves credit before it posts, and Epicor re-checks it after
  // pricing.
  const [credit, setCredit] = useState(null);
  const [creditError, setCreditError] = useState(null);
  const [reservation, setReservation] = useState(null);
  // Every order this console has ever created, not just the one made in this
  // page session. Without it a reload made an order unreachable from the only
  // surface that can clean it up, which is half of what the page is for.
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(null);

  const customers = config?.guard?.customers || [];
  const selected = customers.find(
    (c) => `${c.company}:${c.cust_num}` === custKey,
  );
  const company = selected?.company || '';
  const maxLines = config?.guard?.max_lines ?? 5;
  const maxValue = Number(config?.guard?.max_value ?? 0);
  const canSubmit = Boolean(config?.can_submit);
  // The company of the submission being acted on, which is not necessarily the
  // one selected in the form: a submission picked out of the history carries its
  // own, and cleaning up an AU order against NZ would look for an order that
  // does not exist there.
  const activeCompany = submission?.company || company;

  /* ── Config ── */
  useEffect(() => {
    if (!isAdmin) return;
    getTestOrderConfig().then(setConfig).catch((err) => setConfigError(errText(err)));
  }, [isAdmin]);

  /* ── Warehouse master list (fulfil selector and pickup branches) ── */
  useEffect(() => {
    if (!isAdmin) return;
    listInventoryWarehouses()
      .then((rows) => setWarehouses(rows || []))
      .catch(() => { /* the selectors degrade to Epicor-default only; not fatal */ });
  }, [isAdmin]);

  /* ── History ── */
  const loadHistory = useCallback(async () => {
    try {
      const res = await listSubmissions({ limit: 25 });
      setHistory(res.submissions || []);
      setHistoryError(null);
    } catch (err) {
      // Not fatal: the page can still create an order. Reported rather than
      // swallowed, because an empty list and a failed read look identical.
      setHistoryError(errText(err));
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadHistory();
  }, [isAdmin, loadHistory]);

  /* ── Customer detail (ship-tos), ship-via codes and countries ── */
  useEffect(() => {
    setCustDetail(null);
    setShipToNum('');
    setShipVia([]);
    setShipViaCode('');
    setCountries([]);
    // A warehouse belongs to one company, so a customer switch invalidates the
    // fulfil choice, the pickup branch, and every line stamped from it.
    setFulfilWarehouse('');
    setPickupWarehouse('');
    setQuote(null);
    setLines((prev) => prev.map((l) => ({ ...l, warehouse: null })));
    if (!selected) return;
    let cancelled = false;
    getCustomerDetail(selected.company, selected.cust_num)
      .then((d) => { if (!cancelled) setCustDetail(d); })
      .catch((err) => { if (!cancelled) setError(errText(err)); });
    listShipVia(selected.company)
      .then((d) => { if (!cancelled) setShipVia(d.ship_via || []); })
      .catch(() => { /* the dropdown degrades to free choice; not fatal */ });
    listCountries(selected.company)
      .then((d) => {
        if (cancelled) return;
        const list = d.countries || [];
        setCountries(list);
        // Default the one-time-address country to Australia, matched by name
        // rather than hardcoding CountryNum 1.
        const au = list.find((c) => c.description === 'Australia');
        if (au) {
          setOts((prev) => (prev.country_num ? prev : { ...prev, country_num: String(au.country_num) }));
        }
      })
      .catch(() => { /* the one-time address reports it on submit instead */ });
    return () => { cancelled = true; };
  }, [selected?.company, selected?.cust_num]); // eslint-disable-line react-hooks/exhaustive-deps

  // The default ship-to, once the account's addresses are known. The design
  // shows an address already chosen, and a checkout that opens with an empty
  // required select asks the customer to restate something the account
  // already knows.
  useEffect(() => {
    const first = (custDetail?.ship_tos || [])[0];
    if (first && !shipToNum) setShipToNum(first.ship_to_num);
  }, [custDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Live credit position for the picked customer ── */
  useEffect(() => {
    setCredit(null);
    setCreditError(null);
    if (!selected) return;
    let cancelled = false;
    getLiveCredit(selected.company, selected.cust_num)
      .then((d) => { if (!cancelled) setCredit(d); })
      .catch((err) => { if (!cancelled) setCreditError(errText(err)); });
    return () => { cancelled = true; };
  }, [selected?.company, selected?.cust_num]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── The reservation a submission is holding ── */
  const loadReservation = useCallback(async (row) => {
    if (!row?.external_ref || !row?.company) { setReservation(null); return; }
    try {
      setReservation(await getReservation(row.company, row.external_ref));
    } catch {
      // Not fatal: the submission card still shows the outcome without it.
      setReservation(null);
    }
  }, []);

  useEffect(() => {
    loadReservation(submission);
  }, [submission, loadReservation]);

  const searchParts = useCallback(
    (q) => (company ? searchPricingParts(company, q).then((d) => d.parts || []) : Promise.resolve([])),
    [company],
  );

  /* ── Indicative pricing, per line ── */
  const priceLine = useCallback(async (key, part, qty) => {
    if (!part || !selected) return;
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, pricing: true, priceError: null } : l)));
    try {
      const res = await resolveCustomerPrice(
        selected.company, selected.cust_num, part.part_num, Number(qty) || 1,
      );
      const qtyNum = Number(qty) || 1;
      const tier = (res.tiers || []).reduce(
        (acc, t) => (t.min_qty <= qtyNum ? t : acc), null,
      );
      const unit = tier ? tier.unit_price : res.unit_price_each;
      setLines((prev) => prev.map((l) => (
        l.key === key ? { ...l, price: unit, pricing: false } : l
      )));
    } catch (err) {
      setLines((prev) => prev.map((l) => (
        l.key === key
          ? { ...l, price: null, pricing: false, priceError: errText(err) }
          : l
      )));
    }
  }, [selected]);

  const setLine = (key, patch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  /* ── Warehouses ── */
  // Only enabled warehouses of the picked customer's company are offered; the
  // server re-validates at submit, so a stale tab cannot sneak one through.
  const companyWarehouses = useMemo(
    () => warehouses.filter((w) => w.company === company && w.is_enabled),
    [warehouses, company],
  );

  const applyFulfilWarehouse = (code) => {
    setFulfilWarehouse(code);
    setLines((prev) => prev.map((l) => ({ ...l, warehouse: code || null })));
  };

  const readyLines = useMemo(
    () => lines.filter((l) => l.part && Number(l.qty) > 0),
    [lines],
  );

  const shipTos = custDetail?.ship_tos || [];
  const shipTo = shipTos.find((s) => s.ship_to_num === shipToNum);
  const pickupBranch = companyWarehouses.find((w) => w.warehouse_code === pickupWarehouse);

  /* ── The quote ──
     Debounced, because it moves with every keystroke in a quantity box and
     the endpoint reads inventory and pricing for every line. Superseded
     responses are dropped by sequence number rather than by an abort: two
     quotes in flight can land out of order, and the newer one losing to the
     older is the failure that shows a stale split as current. */
  const quoteSeq = useRef(0);
  const quoteKey = JSON.stringify({
    company,
    cust: selected?.cust_num,
    lines: readyLines.map((l) => [l.part.part_num, Number(l.qty), l.warehouse]),
    deliveryMethod,
    shipToNum: addressMode === 'saved' ? shipToNum : '',
    pickupWarehouse,
    freightService,
  });

  useEffect(() => {
    if (!selected || readyLines.length === 0) {
      setQuote(null);
      setQuoteError(null);
      return undefined;
    }
    const seq = ++quoteSeq.current;
    const timer = setTimeout(() => {
      setQuoting(true);
      quoteOrder({
        company: selected.company,
        cust_num: selected.cust_num,
        delivery_method: deliveryMethod,
        ...(deliveryMethod === 'delivery' && addressMode === 'saved' && shipToNum
          ? { ship_to_num: shipToNum } : {}),
        ...(deliveryMethod === 'collect' && pickupWarehouse
          ? { pickup_warehouse_code: pickupWarehouse } : {}),
        freight_service: freightService,
        lines: readyLines.map((l) => ({
          part_num: l.part.part_num,
          quantity: Number(l.qty),
          ...(l.warehouse ? { warehouse_code: l.warehouse } : {}),
        })),
      })
        .then((d) => {
          if (seq !== quoteSeq.current) return;
          setQuote(d);
          setQuoteError(null);
        })
        .catch((err) => {
          if (seq !== quoteSeq.current) return;
          setQuote(null);
          setQuoteError(errText(err));
        })
        .finally(() => {
          if (seq === quoteSeq.current) setQuoting(false);
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [quoteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Line totals from the staged prices, used for the value cap and as the
  // subtotal before a quote has come back.
  const indicativeTotal = useMemo(
    () => lines.reduce((sum, l) => {
      const unit = Number(l.price);
      const qty = Number(l.qty);
      return sum + (Number.isFinite(unit) && Number.isFinite(qty) ? unit * qty : 0);
    }, 0),
    [lines],
  );

  const overCap = maxValue > 0 && indicativeTotal > maxValue;

  /* ── The quote's per-line split ──
     Matched by position rather than by part number: the endpoint draws two
     lines for the same part from one pool of stock, so their splits differ,
     and a search by part would hand both of them the first line's answer.
     Position is only trusted while the quote still describes this exact cart
     — a superseded one is the wrong shape, and its rows would be read against
     the wrong lines. Anything unmatched falls back to the whole quantity
     being ready, which is what the page shows before the first quote lands
     rather than flashing every line as backordered. */
  const splitByKey = useMemo(() => {
    const rows = quote?.lines || [];
    const aligned = rows.length === readyLines.length;
    const out = new Map();
    readyLines.forEach((line, i) => {
      const row = aligned ? rows[i] : null;
      if (!row || row.part_num !== line.part?.part_num) {
        out.set(line.key, {
          ready: Number(line.qty) || 0,
          back: 0,
          unit: Number(line.price),
          available: null,
          basis: null,
        });
        return;
      }
      out.set(line.key, {
        ready: Number(row.qty_ready),
        back: Number(row.qty_backorder),
        unit: row.unit_price == null ? Number(line.price) : Number(row.unit_price),
        available: Number(row.available_to_sell),
        basis: row.availability_basis,
      });
    });
    return out;
  }, [quote, readyLines]);

  const splitFor = useCallback(
    (line) => splitByKey.get(line.key) || {
      ready: Number(line.qty) || 0,
      back: 0,
      unit: Number(line.price),
      available: null,
      basis: null,
    },
    [splitByKey],
  );

  const backorderLines = useMemo(
    () => readyLines.map((l) => ({ line: l, ...splitFor(l) })).filter((x) => x.back > 0),
    [readyLines, splitFor],
  );
  const shippingLines = useMemo(
    () => readyLines.map((l) => ({ line: l, ...splitFor(l) })).filter((x) => x.ready > 0),
    [readyLines, splitFor],
  );

  /* ── What stops the order being placed ──
     One list, rendered where the button is, rather than an error appearing
     after a click. A disabled button whose reason is elsewhere on the page is
     the thing people file bugs about. */
  const blockers = useMemo(() => {
    const out = [];
    if (!selected) out.push('Choose a trade account in Order setup.');
    if (readyLines.length === 0) out.push('Add at least one line in Order setup.');
    if (deliveryMethod === 'delivery' && addressMode === 'saved' && !shipToNum) {
      out.push('Choose a delivery address.');
    }
    if (deliveryMethod === 'delivery' && addressMode === 'onetime') {
      const missing = ['first_name', 'last_name', 'address1', 'suburb', 'state', 'postcode']
        .some((f) => !ots[f].trim()) || !ots.country_num;
      if (missing) out.push('Complete the one-time delivery address.');
      if (!shipViaCode) out.push('Choose a shipping method for the one-time address.');
    }
    if (deliveryMethod === 'collect' && !pickupWarehouse) {
      out.push('Choose a pickup location.');
    }
    if (paymentMethod === 'credit_card') {
      if (!card.number.trim() || !card.name.trim() || !card.expiry.trim() || !card.cvc.trim()) {
        out.push('Complete the card details.');
      }
    }
    if (!agreeTerms) out.push('Accept the Terms & Conditions and Delivery Policy.');
    if (!canSubmit) {
      out.push(
        config?.write_gate && !config.write_gate.open
          ? 'The write gate is closed, so no order can be created.'
          : 'The customer allowlist is empty, so no order can be created.',
      );
    }
    return out;
  }, [
    selected, readyLines.length, deliveryMethod, addressMode, shipToNum, ots,
    shipViaCode, pickupWarehouse, paymentMethod, card, agreeTerms, canSubmit, config,
  ]);

  /* ── Submit, then poll ── */
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setReadback(null);
    setDeleteConfirm('');

    if (blockers.length > 0) {
      setAttempted(true);
      return;
    }
    setAttempted(false);

    const oneTime = deliveryMethod === 'delivery' && addressMode === 'onetime';
    const externalRef = newExternalRef();
    const payload = {
      company: selected.company,
      cust_num: selected.cust_num,
      external_ref: externalRef,
      delivery_method: deliveryMethod,
      payment_method: paymentMethod,
      ship_complete: shipComplete,
      authority_to_leave: deliveryMethod === 'delivery' && authorityToLeave,
      lines: readyLines.map((l) => ({
        part_num: l.part.part_num,
        quantity: Number(l.qty),
        ...(l.warehouse ? { warehouse_code: l.warehouse } : {}),
      })),
      // What the buyer was shown. Not a pricing instruction -- Epicor prices
      // the order -- but the answer to "what did the screen say" if the split
      // is disputed later, when stock has moved and it cannot be reproduced.
      ...(quote ? { quote_snapshot: quote } : {}),
    };
    if (deliveryMethod === 'delivery') {
      if (oneTime) {
        payload.ots = {
          first_name: ots.first_name.trim(),
          last_name: ots.last_name.trim(),
          address1: ots.address1.trim(),
          suburb: ots.suburb.trim(),
          state: ots.state.trim(),
          postcode: ots.postcode.trim(),
          country_num: Number(ots.country_num),
        };
        if (ots.address2.trim()) payload.ots.address2 = ots.address2.trim();
        // Required for a one-time address; the server validator enforces it too.
        payload.ship_via_code = shipViaCode;
      } else {
        payload.ship_to_num = shipToNum;
        if (shipViaCode) payload.ship_via_code = shipViaCode;
      }
    } else {
      payload.pickup_warehouse_code = pickupWarehouse;
      if (shipViaCode) payload.ship_via_code = shipViaCode;
    }
    if (poNum.trim()) payload.po_num = poNum.trim();
    if (email.trim()) payload.order_email = email.trim();
    if (orderComment.trim()) payload.order_comment = orderComment.trim();

    setSubmitting(true);
    setPlacedQuote(quote);
    try {
      const res = await submitOrder(payload);
      setSubmission(res.submission);
      await poll(selected.company, externalRef);
    } catch (err) {
      // A 422 here is the credit gate refusing before anything was sent: no
      // order exists, and the reservation it placed was released. The detail
      // names the shortfall, so it is shown as worded.
      setError(errText(err));
    } finally {
      setSubmitting(false);
      loadHistory();
      // The reservation and the order both move the customer's position, so
      // re-read it once the submission has settled rather than trusting the
      // figure fetched when the customer was picked.
      if (selected) {
        getLiveCredit(selected.company, selected.cust_num)
          .then(setCredit)
          .catch(() => { /* the position from selection time stands */ });
      }
    }
  };

  const poll = async (co, externalRef) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    /* eslint-disable no-await-in-loop */
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      let row;
      try {
        const res = await listSubmissions({ company: co, external_ref: externalRef });
        row = (res.submissions || [])[0];
      } catch (err) {
        setError(errText(err));
        return;
      }
      if (!row) continue;
      setSubmission(row);
      if (TERMINAL.includes(row.status)) {
        if (row.status === 'submitted' && row.epicor_order_num) {
          await loadReadback(co, row.epicor_order_num);
        }
        return;
      }
    }
    /* eslint-enable no-await-in-loop */
    setNotice(
      'The worker has not settled this submission within two minutes. It is not '
      + 'lost: the ledger row holds the outcome, and a submission stuck in '
      + '"submitting" is resolved by the reconciliation sweep, never by '
      + 'resubmitting.',
    );
  };

  const loadReadback = async (co, orderNum) => {
    setBusyAction('read');
    try {
      setReadback(await readOrderBack(co, orderNum, true));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyAction(null);
    }
  };

  /* ── Picking a past submission ── */
  const selectSubmission = async (row) => {
    setError(null);
    setNotice(null);
    setReadback(null);
    setDeleteConfirm('');
    setSubmission(row);
    // The stored snapshot, so opening a past order shows the figures it was
    // placed with rather than the live quote for whatever is in the form now.
    const stored = requestedPayload(row);
    setPlacedQuote(row.quote_snapshot || stored.quote_snapshot || null);
    if (row.epicor_order_num) {
      await loadReadback(row.company, row.epicor_order_num);
    }
  };

  /* ── Back to a fresh order ── */
  const startNewOrder = () => {
    setSubmission(null);
    setReadback(null);
    setPlacedQuote(null);
    setReservation(null);
    setError(null);
    setNotice(null);
    setAgreeTerms(false);
    setAttempted(false);
    setLines([emptyLine()]);
    setQuote(null);
    setPoNum('');
    setOrderComment('');
    setSetupOpen(true);
  };

  /* ── Cleanup ── */
  const runCleanup = async (action) => {
    const orderNum = submission?.epicor_order_num;
    const co = submission?.company;
    if (!orderNum || !co) return;
    setError(null);
    setNotice(null);
    setBusyAction(action);
    const fn = { close: closeOrder, reopen: reopenOrder, void: voidOrder, delete: deleteOrder }[action];
    try {
      await fn(co, orderNum);
      const refreshed = await listSubmissions({
        company: co, external_ref: submission.external_ref,
      });
      setSubmission((refreshed.submissions || [])[0] || submission);
      setReadback(await readOrderBack(co, orderNum, action === 'void'));
      setDeleteConfirm('');
      loadHistory();
    } catch (err) {
      // A 409 here is Epicor declining to clean up this particular order --
      // allocated releases, a shipment, a legal number. That is an answer about
      // the order, not a bug, and it is shown as Epicor worded it.
      setError(errText(err));
      // Then re-read, because a refusal is not the same as nothing happening. A
      // cleanup that half-took records what took before returning the 409, so the
      // order and the ledger have both moved and the panel would otherwise be
      // showing the state from before a failure that changed things.
      try {
        setReadback(await readOrderBack(co, orderNum, false));
        loadHistory();
      } catch {
        // Already reporting the refusal; a failed re-read on top of it adds
        // nothing an operator can use.
      }
    } finally {
      setBusyAction(null);
    }
  };

  const triggerReconcile = async () => {
    setBusyAction('reconcile');
    try {
      const res = await reconcileOrders({
        company: activeCompany, submission_id: submission?.id,
      });
      setNotice(`Reconciliation queued as job ${res.job_id}. It reads Epicor for the correlation stamp and settles the ledger; it cannot create an order.`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyAction(null);
    }
  };

  /* ── Gates ── */
  if (sessionLoading) return <div className="sync-page" />;
  if (!isAdmin) {
    return (
      <div className="sync-page test-checkout">
        <PageHeader title="Checkout" />
        <Card>
          <p>
            This page creates real sales orders in the production ERP. It is
            restricted to administrators.
          </p>
        </Card>
      </div>
    );
  }

  const gate = config?.write_gate;
  const guard = config?.guard;
  // The state field is a dropdown of Australian states and territories when the
  // one-time address is in Australia, free text for anywhere else.
  const otsCountry = countries.find((c) => String(c.country_num) === String(ots.country_num));
  const otsInAustralia = otsCountry?.description === 'Australia';
  const orderNum = submission?.epicor_order_num;
  const verification = readback?.verification;
  const header = readback?.order?.header;
  // The backend's comparison of what we recorded against what Epicor holds. Kept
  // server-side because it is the same judgement the verification script makes,
  // and two copies of it could disagree about whether an order is neutralised.
  const cleanupState = readback?.state;
  const strandedLines = cleanupState?.reopen_would_strand || [];
  // What the *submission* asked for, not what the form currently shows. Judging
  // this from the live radio buttons meant opening any past delivery order while
  // the form sat on its "collect" default raised a false alarm accusing the push
  // of a bug it did not have.
  const requested = requestedPayload(submission);
  const requestedShipVia = requested.ship_via_code || '';
  const requestedWarehouse = (requested.lines || []).find((l) => l.warehouse_code)?.warehouse_code;

  const placed = submission?.status === 'submitted' && Boolean(orderNum);
  const paymentLabel = {
    on_account: 'On Account',
    credit_card: 'Credit Card',
    paypal: 'PayPal',
  }[submission ? (requested.payment_method || 'on_account') : paymentMethod];

  const freightAmount = quote?.freight?.amount;
  const freightPoa = quote != null && freightAmount == null;
  const setupSummary = selected
    ? `${selected.cust_id || selected.cust_num} · ${readyLines.length} line${readyLines.length === 1 ? '' : 's'}`
    : 'No customer chosen';

  return (
    <div className="sync-page test-checkout">
      <PageHeader
        title="Checkout"
        sub={
          'The order as a customer places it — real stock split, real freight, '
          + 'real credit — against the production Epicor push. The verification '
          + 'and cleanup console is below it, unchanged: this page is the '
          + 'substitute for verify_epicor_order_create.py, which refuses to run '
          + 'against a production instance.'
        }
      />

      {configError && <div className="test-alert error">{configError}</div>}

      <div className="wc-checkout">
        {/* ── Operator setup: the cart this console does not have ── */}
        <div className="wc-setup">
          <button
            type="button"
            className="wc-setup-head"
            onClick={() => setSetupOpen((v) => !v)}
            aria-expanded={setupOpen}
          >
            <IconChevron open={setupOpen} />
            Order setup — operator only
            <span className="wc-setup-summary">{setupSummary}</span>
          </button>
          {setupOpen && (
            <div className="wc-setup-body">
              <p className="wc-section-note" style={{ margin: 0 }}>
                A storefront checkout starts from a cart. This console has none,
                so the customer, the lines and the fulfil warehouse are chosen
                here. Everything below this strip is what a customer would see.
              </p>

              <div className="wc-fields">
                <div>
                  <span className="wc-label">Trade account</span>
                  <select
                    className="wc-select"
                    value={custKey}
                    onChange={(e) => setCustKey(e.target.value)}
                  >
                    <option value="">Select an allowlisted customer…</option>
                    {customers.map((c) => (
                      <option key={`${c.company}:${c.cust_num}`} value={`${c.company}:${c.cust_num}`}>
                        {c.company} · {c.cust_id || c.cust_num} · {c.name || '(unnamed)'}
                        {c.credit_hold ? ' · CREDIT HOLD' : ''}
                      </option>
                    ))}
                  </select>
                  <span className="wc-section-note">
                    A dropdown rather than a search box: the allowlist is the
                    whole safety argument, so the page offers nothing outside it.
                  </span>
                </div>
                <div>
                  <span className="wc-label">Fulfil all lines from</span>
                  <select
                    className="wc-select"
                    value={fulfilWarehouse}
                    disabled={!company}
                    onChange={(e) => applyFulfilWarehouse(e.target.value)}
                  >
                    <option value="">Let Epicor default it</option>
                    {companyWarehouses.map((w) => (
                      <option key={w.warehouse_code} value={w.warehouse_code}>
                        {w.warehouse_code} · {w.display_name || w.description}
                      </option>
                    ))}
                  </select>
                  <span className="wc-section-note">
                    Stamped onto every line. Epicor holds the warehouse per line
                    and derives the release plant from it, and the ready-to-ship
                    split is measured against this warehouse&apos;s stock.
                  </span>
                </div>
                <div>
                  <span className="wc-label">Contact email (optional)</span>
                  <input
                    className="wc-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={100}
                    placeholder="buyer@example.com"
                  />
                  <span className="wc-section-note">
                    Folded into the Epicor order comment — Epicor takes contact
                    emails from the customer record and has no field for one on
                    the order itself.
                  </span>
                </div>
              </div>

              <div className="wc-setup-lines">
                {lines.map((l, i) => (
                  <div className="wc-setup-line" key={l.key}>
                    <TypeAhead
                      placeholder={company ? 'Search parts…' : 'Pick a customer first'}
                      disabled={!company}
                      search={searchParts}
                      display={(p) => `${p.part_num} — ${p.part_description || ''}`}
                      pickedLabel={l.part ? `${l.part.part_num} — ${l.part.part_description || ''}` : ''}
                      renderOption={(p) => (
                        <>
                          <strong>{p.part_num}</strong>
                          <div className="test-muted">{p.part_description}</div>
                        </>
                      )}
                      onPick={(p) => {
                        setLine(l.key, { part: p, price: null, priceError: null });
                        if (p) priceLine(l.key, p, l.qty);
                      }}
                    />
                    <input
                      className="wc-input"
                      type="number"
                      min="1"
                      step="1"
                      value={l.qty}
                      onChange={(e) => setLine(l.key, { qty: e.target.value })}
                      onBlur={() => l.part && priceLine(l.key, l.part, l.qty)}
                      aria-label={`Quantity for line ${i + 1}`}
                    />
                    <SetupStock line={l} split={splitByKey.get(l.key)} />
                    <div className="wc-setup-line-price">
                      {l.pricing ? '…' : l.priceError
                        ? <span className="wc-setup-warn" title={l.priceError}>no price</span>
                        : l.price != null ? money(l.price) : '—'}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={lines.length === 1}
                      onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>

              <div className="wc-setup-foot">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={lines.length >= maxLines}
                  onClick={() => setLines((prev) => (
                    [...prev, { ...emptyLine(), warehouse: fulfilWarehouse || null }]
                  ))}
                >
                  Add line
                </Button>
                <span className="wc-section-note">
                  {maxLines} lines and {money(maxValue)} maximum. The value cap is
                  applied to Epicor&apos;s own prices immediately before Update,
                  not to the total below.
                </span>
                {overCap && (
                  <span className="wc-setup-warn">
                    {money(indicativeTotal)} is above the cap; this will very
                    likely be refused before Update.
                  </span>
                )}
                {quote?.unpriced_parts?.length > 0 && (
                  <span className="wc-setup-warn">
                    No staged price for {quote.unpriced_parts.join(', ')} — the
                    totals below exclude {quote.unpriced_parts.length === 1 ? 'it' : 'them'}.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Placed ── */}
        {placed ? (
          <PlacedOrder
            submission={submission}
            quote={placedQuote}
            paymentLabel={paymentLabel}
            pickup={requested.delivery_method === 'collect'}
            onNewOrder={startNewOrder}
          />
        ) : readyLines.length === 0 ? (
          <div className="wc-empty">
            <span className="wc-empty-icon"><IconCart size={26} /></span>
            <h2>Your cart is empty</h2>
            <p>
              There is nothing to check out yet. Add a customer and some lines in
              Order setup above.
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="wc-checkout-grid">
              <div className="wc-col-main">

                {/* ── 1. Delivery Method ── */}
                <section className="wc-section">
                  <div className="wc-section-head">
                    <span className="wc-step">1</span>
                    <h2>Delivery Method</h2>
                  </div>

                  <div className="wc-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={deliveryMethod === 'delivery'}
                      className={`wc-tab${deliveryMethod === 'delivery' ? ' is-active' : ''}`}
                      onClick={() => { setDeliveryMethod('delivery'); setPickupWarehouse(''); setShipViaCode(''); }}
                    >
                      Deliver
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={deliveryMethod === 'collect'}
                      className={`wc-tab${deliveryMethod === 'collect' ? ' is-active' : ''}`}
                      onClick={() => {
                        setDeliveryMethod('collect');
                        setShipToNum('');
                        setShipViaCode('');
                        setAuthorityToLeave(false);
                      }}
                    >
                      Collect
                    </button>
                  </div>

                  {deliveryMethod === 'delivery' ? (
                    <>
                      <div className="wc-field-row">
                        <span className="wc-label">Select Address</span>
                        <button
                          type="button"
                          className="wc-link-button"
                          onClick={() => setAddressMode(addressMode === 'saved' ? 'onetime' : 'saved')}
                          style={{
                            appearance: 'none', border: 'none', background: 'none',
                            color: 'var(--text-link)', cursor: 'pointer', font: 'inherit',
                            padding: 0,
                          }}
                        >
                          {addressMode === 'saved' ? 'Add a new address' : 'Use a saved address'}
                        </button>
                      </div>

                      {addressMode === 'saved' ? (
                        <>
                          <select
                            className="wc-select"
                            value={shipToNum}
                            onChange={(e) => setShipToNum(e.target.value)}
                          >
                            {/* Only when nothing is chosen. The account's main
                                address has an empty ShipToNum, so a permanent
                                placeholder would share its value and win the
                                match — leaving the select reading "Select an
                                address" while the panel below displayed the
                                address that was in fact selected. */}
                            {!shipTo && <option value="">Select an address…</option>}
                            {shipTos.map((s) => (
                              <option key={s.ship_to_num} value={s.ship_to_num}>
                                {s.name} — {addressLine(s)}
                              </option>
                            ))}
                          </select>
                          {shipTo && (
                            <div className="wc-panel">
                              <span className="wc-panel-icon"><IconPin /></span>
                              <div className="wc-panel-body">
                                <div className="wc-panel-title">{shipTo.name}</div>
                                <div className="wc-panel-line">{addressLine(shipTo)}</div>
                                {shipTo.phone_num && (
                                  <div className="wc-panel-meta">{shipTo.phone_num}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <OneTimeAddress
                          ots={ots}
                          setOts={setOts}
                          countries={countries}
                          inAustralia={otsInAustralia}
                        />
                      )}

                      <h3 className="wc-eyebrow">Shipping Options</h3>
                      <div className="wc-ship-option">
                        <div className="wc-ship-option-body">
                          <div className="wc-ship-option-title">
                            {freightService === 'express' ? 'Express Delivery' : 'Standard Delivery'}
                          </div>
                          <div className="wc-ship-option-sub">
                            {quoting ? 'Calculating…'
                              : freightPoa ? (quote.freight.detail || 'Price on application.')
                                : quote?.freight?.zone
                                  ? `${quote.freight.zone === 'metro' ? 'Metro' : 'Regional'} rate for this address.`
                                  : 'Rate resolved from the delivery postcode.'}
                          </div>
                        </div>
                        <div className="wc-ship-option-right">
                          {quote?.freight?.zone && (
                            <span className="wc-badge is-muted">{quote.freight.zone}</span>
                          )}
                          <span className="wc-price">
                            {quoting ? '…' : freightPoa ? 'POA' : money(freightAmount)}
                          </span>
                        </div>
                      </div>
                      {Number(freightAmount) === 0 && !freightPoa && (
                        <p className="wc-free-note">
                          This address attracts no freight charge.
                        </p>
                      )}

                      <div className="wc-fields" style={{ marginTop: 16 }}>
                        <div>
                          <span className="wc-label">Service</span>
                          <select
                            className="wc-select"
                            value={freightService}
                            onChange={(e) => setFreightService(e.target.value)}
                          >
                            <option value="standard">Standard</option>
                            <option value="express">Express</option>
                          </select>
                        </div>
                        <div>
                          <span className="wc-label">
                            {addressMode === 'onetime' ? 'Carrier' : 'Carrier (optional)'}
                          </span>
                          <select
                            className="wc-select"
                            value={shipViaCode}
                            onChange={(e) => setShipViaCode(e.target.value)}
                          >
                            <option value="">
                              {addressMode === 'onetime'
                                ? 'Select a carrier…'
                                : 'Let Epicor default it from the address'}
                            </option>
                            {shipVia.map((s) => (
                              <option key={s.ship_via_code} value={s.ship_via_code}>
                                {s.ship_via_code} · {s.description}
                              </option>
                            ))}
                          </select>
                          <span className="wc-section-note">
                            {addressMode === 'onetime'
                              ? 'Required for a one-time address: with no ship-to record there is nothing for Epicor to default the carrier from.'
                              : 'Blank is what 2,930 of the incumbent\u2019s web orders do.'}
                          </span>
                        </div>
                      </div>

                      <div className="wc-options">
                        <label className="wc-option">
                          <input
                            type="checkbox"
                            checked={authorityToLeave}
                            onChange={(e) => setAuthorityToLeave(e.target.checked)}
                          />
                          <span className="wc-option-body">
                            <span className="wc-option-title">Authority to Leave</span>
                            <p className="wc-option-note">
                              I authorise the carrier to leave my package in a safe
                              place if I am not available to receive it. I
                              understand that I assume responsibility for the
                              package once it has been delivered.
                            </p>
                          </span>
                        </label>
                        <label className="wc-option">
                          <input
                            type="checkbox"
                            checked={shipComplete}
                            onChange={(e) => setShipComplete(e.target.checked)}
                          />
                          <span className="wc-option-body">
                            <span className="wc-option-title">Ship Complete</span>
                            <p className="wc-option-note">
                              Hold my order until all items are available and ship
                              everything together.
                            </p>
                          </span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="wc-label">Select Pickup Location</span>
                      <select
                        className="wc-select"
                        value={pickupWarehouse}
                        disabled={!company}
                        onChange={(e) => setPickupWarehouse(e.target.value)}
                      >
                        <option value="">Select a branch…</option>
                        {companyWarehouses.map((w) => (
                          <option key={w.warehouse_code} value={w.warehouse_code}>
                            {w.display_name || w.description}
                            {w.city ? ` — ${w.city}` : ''}
                          </option>
                        ))}
                      </select>

                      {pickupBranch && (
                        <div className="wc-panel">
                          <span className="wc-panel-icon"><IconStore /></span>
                          <div className="wc-panel-body">
                            <div className="wc-panel-title">
                              {pickupBranch.display_name || pickupBranch.description}
                            </div>
                            {addressLine(pickupBranch) && (
                              <div className="wc-panel-line">{addressLine(pickupBranch)}</div>
                            )}
                            {pickupBranch.phone && (
                              <div className="wc-panel-meta">Tel: {pickupBranch.phone}</div>
                            )}
                            {pickupBranch.operating_hours && (
                              <div className="wc-panel-line">
                                Collect between {pickupBranch.operating_hours}
                              </div>
                            )}
                            <p className="wc-panel-note">
                              Collections are available two hours after the order is
                              placed for orders made before 1pm on weekdays. We
                              email you once the order is ready.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="wc-options">
                        <label className="wc-option">
                          <input
                            type="checkbox"
                            checked={shipComplete}
                            onChange={(e) => setShipComplete(e.target.checked)}
                          />
                          <span className="wc-option-body">
                            <span className="wc-option-title">Collect Complete</span>
                            <p className="wc-option-note">
                              Hold my order until all items are available and
                              notify me once.
                            </p>
                          </span>
                        </label>
                      </div>
                    </>
                  )}
                </section>

                {/* ── 2. Order Details ── */}
                <section className="wc-section">
                  <div className="wc-section-head">
                    <span className="wc-step">2</span>
                    <h2>Order Details</h2>
                    <span className="wc-badge is-muted">Optional</span>
                  </div>

                  <div className="wc-field">
                    <span className="wc-label">Purchase Order Number</span>
                    <input
                      className="wc-input"
                      value={poNum}
                      maxLength={50}
                      onChange={(e) => setPoNum(e.target.value)}
                      placeholder="Your reference for this order"
                    />
                  </div>

                  <div className="wc-field" style={{ marginTop: 16 }}>
                    <span className="wc-label">Order notes</span>
                    <textarea
                      className="wc-textarea"
                      value={orderComment}
                      maxLength={NOTES_MAX}
                      onChange={(e) => setOrderComment(e.target.value)}
                      placeholder="Anything the warehouse should know about this order"
                    />
                    <div className="wc-charcount">{orderComment.length}/{NOTES_MAX}</div>
                    <span className="wc-section-note">
                      Posted to Epicor as the order comment, with the contact
                      email appended when one is given. Delivery and collection
                      instructions travel separately, in the shipping comment the
                      warehouse and the carrier read.
                    </span>
                  </div>
                </section>

                {/* ── 3. Payment Method ── */}
                <section className={`wc-section${attempted && paymentMethod === 'credit_card' && blockers.includes('Complete the card details.') ? ' is-invalid' : ''}`}>
                  <div className="wc-section-head">
                    <span className="wc-step">3</span>
                    <h2>Payment Method</h2>
                  </div>
                  <p className="wc-section-note" style={{ marginBottom: 16 }}>
                    Only the items ready to ship are charged now. Backordered
                    items are billed on fulfilment.
                  </p>

                  <div className="wc-pay-rows">
                    <label className={`wc-pay-row${paymentMethod === 'on_account' ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'on_account'}
                        onChange={() => setPaymentMethod('on_account')}
                      />
                      <span className="wc-pay-body">
                        <span className="wc-pay-title">On Account</span>
                        <span className="wc-pay-sub">
                          {creditError ? `Credit position unavailable: ${creditError}`
                            : !credit ? 'Reading your account…'
                              : credit.available_credit == null
                                ? 'Available balance not assessable'
                                : `Available balance: ${money(credit.available_credit)}`}
                        </span>
                      </span>
                      <span className="wc-pay-aside">
                        {credit?.terms_code || custDetail?.customer?.terms_code || ''}
                      </span>
                    </label>

                    <label className={`wc-pay-row${paymentMethod === 'credit_card' ? ' is-selected' : ''}${attempted && paymentMethod === 'credit_card' && blockers.includes('Complete the card details.') ? ' is-invalid' : ''}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'credit_card'}
                        onChange={() => setPaymentMethod('credit_card')}
                      />
                      <span className="wc-pay-body">
                        <span className="wc-pay-title">Credit Card</span>
                        <span className="wc-pay-sub">Pay using Visa, Mastercard or Amex</span>
                      </span>
                    </label>

                    <label className={`wc-pay-row${paymentMethod === 'paypal' ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === 'paypal'}
                        onChange={() => setPaymentMethod('paypal')}
                      />
                      <span className="wc-pay-body">
                        <span className="wc-pay-title">PayPal</span>
                        <span className="wc-pay-sub">Pay with your PayPal account</span>
                      </span>
                    </label>
                  </div>

                  {paymentMethod === 'credit_card' && (
                    <div className="wc-card-fields">
                      <div className="wc-field">
                        <span className="wc-label">Card number</span>
                        <input
                          className="wc-input"
                          value={card.number}
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="0000 0000 0000 0000"
                          onChange={(e) => setCard((c) => ({ ...c, number: e.target.value }))}
                        />
                      </div>
                      <div className="wc-field" style={{ marginTop: 12 }}>
                        <span className="wc-label">Name on card</span>
                        <input
                          className="wc-input"
                          value={card.name}
                          autoComplete="off"
                          onChange={(e) => setCard((c) => ({ ...c, name: e.target.value }))}
                        />
                      </div>
                      <div className="wc-fields" style={{ marginTop: 12 }}>
                        <div className="wc-narrow">
                          <span className="wc-label">Expiry</span>
                          <input
                            className="wc-input"
                            value={card.expiry}
                            placeholder="MM/YY"
                            autoComplete="off"
                            onChange={(e) => setCard((c) => ({ ...c, expiry: e.target.value }))}
                          />
                        </div>
                        <div className="wc-narrow">
                          <span className="wc-label">CVC</span>
                          <input
                            className="wc-input"
                            value={card.cvc}
                            placeholder="123"
                            autoComplete="off"
                            onChange={(e) => setCard((c) => ({ ...c, cvc: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="wc-card-secure">
                        <IconLock size={15} />
                        Card details are encrypted and never stored by Waterco.
                      </div>
                    </div>
                  )}

                  {/* The one thing on this page that is not what it appears to
                      be, said plainly rather than left to be discovered. There
                      is no payment rail: every order lands on the account in
                      Epicor whichever method is chosen, so the credit gate
                      applies to all three. */}
                  <div className="wc-alert is-info" style={{ marginTop: 16 }}>
                    <div className="wc-alert-title">No payment is taken here</div>
                    The method is recorded on the submission and nothing is sent
                    to a payment rail, because none is connected yet. Every order
                    lands on the trade account in Epicor whichever option is
                    chosen, which is why the credit gate applies to all three.
                  </div>

                  {attempted && paymentMethod === 'credit_card'
                    && blockers.includes('Complete the card details.') && (
                    <div className="wc-alert is-danger" style={{ marginTop: 12 }}>
                      Enter the card number, the name on the card, the expiry and
                      the CVC before placing the order.
                    </div>
                  )}
                </section>
              </div>

              {/* ── Order Summary ── */}
              <aside className={`wc-col-side${quote?.has_backorder ? ' is-tall' : ''}`}>
                <div className="wc-summary">
                  <div className="wc-summary-head">
                    <h2>Order Summary</h2>
                    <button
                      type="button"
                      onClick={() => setSetupOpen(true)}
                      style={{
                        appearance: 'none', border: 'none', background: 'none',
                        color: 'var(--text-link)', cursor: 'pointer',
                        font: '400 14px/1.45 var(--wc-font-sans)', padding: 0,
                      }}
                    >
                      Edit Cart
                    </button>
                  </div>

                  <div className="wc-summary-count">
                    <span>
                      {shippingLines.length} item{shippingLines.length === 1 ? '' : 's'} ready to ship
                    </span>
                    {quoting && <span className="wc-badge is-muted">updating…</span>}
                  </div>

                  <div className="wc-summary-items">
                    {shippingLines.map(({ line, ready, unit }) => (
                      <SummaryItem
                        key={line.key}
                        name={line.part.part_num}
                        desc={line.part.part_description}
                        qty={ready}
                        amount={Number.isFinite(unit) ? unit * ready : null}
                      />
                    ))}
                    {shippingLines.length === 0 && (
                      <p className="wc-section-note" style={{ margin: 0 }}>
                        Nothing on this order can ship today. Everything is on
                        backorder — see below.
                      </p>
                    )}
                  </div>

                  <div className="wc-rule" />

                  <div className="wc-totals">
                    <div className="wc-total-row">
                      <span>Subtotal (Ready to ship)</span>
                      <span>{quote ? money(quote.ready.subtotal) : money(indicativeTotal)}</span>
                    </div>
                    <div className="wc-total-row">
                      <span>{deliveryMethod === 'collect' ? 'Collection' : 'Delivery'}</span>
                      <span>
                        {!quote ? '—' : freightPoa ? 'POA' : money(quote.ready.freight)}
                      </span>
                    </div>
                    <div className="wc-total-row">
                      <span>Tax (GST)</span>
                      <span>{quote ? money(quote.ready.gst) : '—'}</span>
                    </div>
                  </div>

                  <div className="wc-paytoday">
                    <div className="wc-paytoday-row">
                      <span className="wc-paytoday-label">Pay today</span>
                      <span className="wc-paytoday-value">
                        {quote ? money(quote.pay_today) : '—'}
                      </span>
                    </div>
                    {quote?.has_backorder && (
                      <div className="wc-paytoday-later">
                        <span>Backorder (charged later)</span>
                        <span>{money(quote.charged_later)}</span>
                      </div>
                    )}
                  </div>

                  {quoteError && (
                    <div className="wc-blockers">
                      <div className="wc-alert is-warning">
                        The totals could not be calculated: {quoteError}
                      </div>
                    </div>
                  )}
                  {quote?.stock?.stale && (
                    <div className="wc-blockers">
                      <div className="wc-alert is-warning">
                        Stock figures may be out of date
                        {quote.stock.last_watermark_update
                          ? ` — the last inventory sync was ${new Date(quote.stock.last_watermark_update).toLocaleString()}.`
                          : '.'}
                        {' '}The ready-to-ship split below may move by the time
                        the order is picked.
                      </div>
                    </div>
                  )}

                  <label className={`wc-terms${attempted && !agreeTerms ? ' is-invalid' : ''}`}>
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the <a href="https://www.waterco.com.au/terms-conditions" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>
                      {' '}and <a href="https://www.waterco.com.au/delivery-policy" target="_blank" rel="noreferrer">Delivery Policy</a>.
                    </span>
                  </label>

                  {attempted && blockers.length > 0 && (
                    <div className="wc-blockers">
                      <div className="wc-alert is-danger">
                        <div className="wc-alert-title">
                          {blockers.length === 1
                            ? 'One thing is missing'
                            : `${blockers.length} things are missing`}
                        </div>
                        <ul>
                          {blockers.map((b) => <li key={b}>{b}</li>)}
                        </ul>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="wc-blockers">
                      <div className="wc-alert is-danger">{error}</div>
                    </div>
                  )}
                  {notice && (
                    <div className="wc-blockers">
                      <div className="wc-alert is-warning">{notice}</div>
                    </div>
                  )}

                  <div className="wc-place">
                    <button
                      type="submit"
                      className="wc-btn-place"
                      disabled={submitting}
                    >
                      {submitting ? 'Placing order…' : 'Place Order'}
                    </button>
                    <p className="wc-pay-note">
                      {quote?.has_backorder
                        ? `${money(quote.pay_today)} charged now · ${money(quote.charged_later)} when the backorder ships`
                        : quote
                          ? `${money(quote.pay_today)} on ${paymentLabel}`
                          : 'Totals appear once the order is priced'}
                    </p>
                  </div>
                </div>

                {/* ── On Backorder ── */}
                {quote?.has_backorder && (
                  <div className="wc-backorder">
                    <div className="wc-backorder-head">
                      <span className="wc-backorder-icon"><IconAlert /></span>
                      <h2>On Backorder</h2>
                      <span className="wc-backorder-total">{money(quote.backorder.total)}</span>
                    </div>
                    <p className="wc-backorder-note">
                      Backorders are paid for when the items are ready to be picked
                      for shipping. The backorder payment method is the same as the
                      original order.
                    </p>
                    <div className="wc-backorder-items">
                      {backorderLines.map(({ line, back, unit }) => (
                        <SummaryItem
                          key={line.key}
                          name={line.part.part_num}
                          desc={line.part.part_description}
                          qty={back}
                          amount={Number.isFinite(unit) ? unit * back : null}
                          backorder
                        />
                      ))}
                    </div>
                    <div className="wc-backorder-totals">
                      <div className="wc-total-row">
                        <span>Subtotal</span>
                        <span>{money(quote.backorder.subtotal)}</span>
                      </div>
                      <div className="wc-total-row">
                        <span>Tax (GST)</span>
                        <span>{money(quote.backorder.gst)}</span>
                      </div>
                      <div className="wc-total-row is-grand">
                        <span>Total</span>
                        <span>{money(quote.backorder.total)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="wc-trust">
                  <span><IconShield size={16} /> Australian owned</span>
                  <span><IconCheck size={16} /> Industry leading warranty</span>
                  <span><IconLock size={16} /> Expert support team</span>
                  <span><IconTruck size={16} /> Fast nationwide delivery</span>
                </div>
              </aside>
            </div>

          </form>
        )}
      </div>

      {/* ============================================================
          Below the checkout: the verification console.

          Everything from here down is the operator surface, and it is
          deliberately in the console's own styling rather than the
          storefront's — what a customer would see ends above this line.
          ============================================================ */}

      {/* ── Posture ── */}
      {config && (
        <Card title="What this page may do">
          <div className="test-posture">
            <div>
              <span className="test-posture-label">Write gate</span>
              {gate.open
                ? <Badge variant="success">open on {gate.instance}</Badge>
                : <Badge variant="danger">closed</Badge>}
              {!gate.open && <p className="test-muted">{gate.blocked_reason}</p>}
            </div>
            <div>
              <span className="test-posture-label">Customer allowlist</span>
              {guard.allowlist_disabled
                ? <Badge variant="warning">lifted — any customer</Badge>
                : guard.blocks_everything
                  ? <Badge variant="danger">empty — blocks everything</Badge>
                  : (
                    <Badge variant="success">
                      {guard.customers.length === 1
                        ? '1 customer'
                        : `${guard.customers.length} customers`}
                    </Badge>
                  )}
              {guard.blocks_everything && !guard.allowlist_disabled && (
                <p className="test-muted">
                  EPICOR_ORDER_CUSTOMER_ALLOWLIST is unset. It fails closed on
                  purpose: opening the write gate without naming the test
                  customers must not permit arbitrary ones.
                </p>
              )}
            </div>
            <div>
              <span className="test-posture-label">Caps</span>
              <span>
                {maxLines} lines, {fmtMoney(maxValue)} — the value cap is applied
                to Epicor&apos;s own prices immediately before Update, not to the
                indicative total above.
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* ── Everything this console has created ── */}
      <Card
        title="Orders this console has created"
        actions={
          <Button variant="secondary" size="sm" onClick={loadHistory}>
            Refresh
          </Button>
        }
      >
        <p className="test-muted">
          Pick one to read it back from Epicor and clean it up. An order stays
          reachable here after a reload, which is the only way cleanup is any use:
          a closed, voided order is the tidy end state, and it cannot be reached
          from a page that has forgotten the order number.
        </p>
        {historyError && <div className="test-alert error">{historyError}</div>}
        {!historyError && history.length === 0 && (
          <p className="test-muted">Nothing submitted yet.</p>
        )}
        {history.length > 0 && (
          <table className="test-history">
            <thead>
              <tr>
                <th>Created</th>
                <th>Ref</th>
                <th>Customer</th>
                <th>Fulfilment</th>
                <th>Status</th>
                <th>Order</th>
                <th>In Epicor</th>
                <th aria-label="Select" />
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr
                  key={row.id}
                  className={submission?.id === row.id ? 'is-selected' : undefined}
                >
                  <td className="test-muted">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                  </td>
                  <td><code>{row.external_ref}</code></td>
                  <td>{row.company} · {row.cust_num}</td>
                  <td>
                    {row.pickup_warehouse_code
                      ? <Badge variant="neutral">collect {row.pickup_warehouse_code}</Badge>
                      : <span className="test-muted">delivery</span>}
                    {row.ship_complete && <Badge variant="neutral">ship complete</Badge>}
                    {row.authority_to_leave && <Badge variant="neutral">ATL</Badge>}
                  </td>
                  <td>
                    <Badge variant={
                      row.status === 'submitted' ? 'success'
                        : row.status === 'failed' ? 'danger'
                          : row.status === 'needs_review' ? 'warning' : 'neutral'
                    }>{row.status}</Badge>
                  </td>
                  <td><strong>{row.epicor_order_num || '—'}</strong></td>
                  <td>
                    {row.cleanup_action
                      ? <Badge variant="neutral">{row.cleanup_action}</Badge>
                      : row.epicor_order_num
                        ? <span className="test-muted">live</span>
                        : <span className="test-muted">—</span>}
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={Boolean(busyAction)}
                      onClick={() => selectSubmission(row)}
                    >
                      {submission?.id === row.id ? 'Selected' : 'Open'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Outcome ── */}
      {submission && (
        <Card
          title="Submission"
          actions={
            <Button variant="secondary" size="sm" onClick={startNewOrder}>
              Start a new order
            </Button>
          }
        >
          <div className="test-kv">
            <div><span>Ref</span><code>{submission.external_ref}</code></div>
            <div><span>Company</span><strong>{submission.company}</strong></div>
            <div><span>Status</span><Badge variant={
              submission.status === 'submitted' ? 'success'
                : submission.status === 'failed' ? 'danger'
                  : submission.status === 'needs_review' ? 'warning' : 'neutral'
            }>{submission.status}</Badge></div>
            <div><span>Order</span><strong>{orderNum || '—'}</strong></div>
            <div><span>Stamp</span><code>{submission.stamp_field}={submission.stamp_token}</code></div>
            {requested.order_email && (
              <div><span>Email</span><strong>{requested.order_email}</strong></div>
            )}
            <div>
              <span>Payment</span>
              <strong>{(requested.payment_method || 'on_account').replace(/_/g, ' ')}</strong>
            </div>
            <div>
              <span>Fulfilment</span>
              <strong>
                {requested.delivery_method === 'collect'
                  ? `collect${submission.pickup_warehouse_code ? ` from ${submission.pickup_warehouse_code}` : ''}`
                  : requested.delivery_method || 'unspecified'}
              </strong>
            </div>
            {submission.ship_complete && (
              <div><span>Ship complete</span><Badge variant="neutral">requested</Badge></div>
            )}
            {submission.authority_to_leave && (
              <div><span>Authority to leave</span><Badge variant="neutral">authorised</Badge></div>
            )}
            {requestedWarehouse && (
              <div><span>Fulfil from</span><strong>{requestedWarehouse}</strong></div>
            )}
            {requested.ots && (
              <div><span>One-time address</span><strong>{formatOts(requested.ots)}</strong></div>
            )}
            {submission.cleanup_action && (
              <div><span>Cleanup</span><Badge variant="neutral">{submission.cleanup_action}</Badge></div>
            )}
            {/* The credit hold this submission placed. Active until the order
                lands (released 'landed') or the push fails (released
                'cancelled'); shown so the customer's suppressed headroom is
                visible while it is suppressed. */}
            {reservation && (
              <div>
                <span>Credit hold</span>
                <strong>
                  {fmtMoney(reservation.amount)}
                  {reservation.currency_code ? ` ${reservation.currency_code}` : ''}
                </strong>
                {' '}
                {reservation.status === 'active'
                  ? <Badge variant="warning">held</Badge>
                  : (
                    <Badge variant="neutral">
                      {reservation.status}
                      {reservation.released_reason ? ` · ${reservation.released_reason}` : ''}
                    </Badge>
                  )}
              </div>
            )}
          </div>
          {submission.last_error_detail && (
            <div className="test-alert error">{submission.last_error_detail}</div>
          )}
          {(submission.status === 'needs_review' || submission.status === 'submitting') && (
            <div className="test-alert notice">
              <p>
                The outcome of Update is unknown, so the order may exist. It must
                never be resubmitted — only the reconciliation sweep, which looks
                for the correlation stamp in Epicor, can settle this.
              </p>
              <Button
                variant="secondary"
                size="sm"
                disabled={busyAction === 'reconcile'}
                onClick={triggerReconcile}
              >
                {busyAction === 'reconcile' ? 'Queueing…' : 'Queue reconciliation'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ── Verification: the four questions ── */}
      {orderNum && (
        <Card
          title="Verification"
          actions={
            <Button
              variant="secondary"
              size="sm"
              disabled={busyAction === 'read'}
              onClick={() => loadReadback(activeCompany, orderNum)}
            >
              {busyAction === 'read' ? 'Reading…' : 'Re-read from Epicor'}
            </Button>
          }
        >
          <Question
            n={1}
            title="Does Update return the assigned OrderNum?"
            pass={Boolean(orderNum)}
            detail={orderNum
              ? `Yes — Epicor assigned order ${orderNum} and the push read it from the Update response.`
              : 'No number came back, so the order had to be resolved by the stamp instead.'}
          />
          <Question
            n={2}
            title="Does the correlation stamp survive the commit?"
            pass={verification?.stamp?.pass}
            emphasis
            detail={
              verification?.stamp?.pass
                ? `Yes — ${verification.stamp.field} reads back as ${verification.stamp.stored} over ODBC, and the reconciliation lookup resolves it to order ${orderNum}. The recovery path works end to end.`
                : verification?.stamp?.detail
                  || 'Not yet read. This is the disqualifying question: if the stamp does not come back, the recovery mechanism is inoperative and the push must not stay enabled.'
            }
          />
          <Question
            n={3}
            title="Does our order match one the incumbent integration produced?"
            pass={verification?.parity?.reference_order_num
              ? (verification.parity.header || []).every((r) => r.match)
              : null}
            detail={verification?.parity?.reference_order_num
              ? `Compared against order ${verification.parity.reference_order_num}.`
              : verification?.parity?.detail || 'Not yet read.'}
          >
            {verification?.parity?.reference_order_num && (
              <ParityTable
                header={verification.parity.header}
                lines={verification.parity.lines}
              />
            )}
          </Question>
          {/* Derived from Epicor's header, not from our own cleanup_action. It
              used to read the ledger, so it reported a pass whenever we had
              recorded a void — which is our claim, not evidence. Six orders
              passed that way while every one stayed open. */}
          <Question
            n={4}
            title="Can the order be taken out of play?"
            pass={cleanupState ? cleanupState.neutralised : null}
            emphasis={Boolean(cleanupState?.stranded)}
            detail={(() => {
              if (!cleanupState) return 'Not yet read from Epicor.';
              if (cleanupState.stranded) return cleanupState.detail;
              if (cleanupState.neutralised) {
                return `Yes — Epicor reads OpenOrder=false and VoidOrder=${String(cleanupState.void_order)}. CloseOrder neutralised it: on an unshipped order Epicor implements a close as a void, cascading to the lines and releases and zeroing the total.`;
              }
              return 'Unanswered until the order is closed or voided below. Note that setting VoidOrder alone does not answer it: the flag is writable and cascades nothing, so a void-but-open order is still live.';
            })()}
          />

          {/* Not one of the four. The four ask whether the push mechanism works;
              this asks whether the customer's own instructions survived it,
              which is a different failure and invisible without looking — an
              order missing its ship-complete hold looks completely normal. */}
          {verification?.fulfilment && (
            <Question
              n="+"
              title="Did the fulfilment instructions land?"
              pass={verification.fulfilment.pass}
              detail={verification.fulfilment.detail}
            >
              <table className="test-table">
                <thead>
                  <tr><th>Instruction</th><th>Requested</th><th>Held by Epicor</th><th>Match</th></tr>
                </thead>
                <tbody>
                  {verification.fulfilment.checks.map((c) => (
                    <tr key={c.field} className={c.match ? '' : 'mismatch'}>
                      <td><code>{c.field}</code></td>
                      <td>{String(c.requested)}</td>
                      <td>{String(c.stored)}</td>
                      <td>{c.match ? 'yes' : 'NO'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {verification.fulfilment.ship_comment && (
                <p className="test-muted">
                  ShipComment reads: {verification.fulfilment.ship_comment}
                </p>
              )}
            </Question>
          )}

          {header && (
            <div className="test-readback">
              <h4>As Epicor holds it</h4>
              <div className="test-kv">
                <div><span>ShipViaCode</span><strong>{header.ShipViaCode || '(blank)'}</strong></div>
                <div><span>ShipToNum</span><strong>{header.ShipToNum || '(main)'}</strong></div>
                <div><span>VoidOrder</span><strong>{String(Boolean(header.VoidOrder))}</strong></div>
                <div className={cleanupState?.stranded ? 'is-bad' : undefined}>
                  <span>OpenOrder</span>
                  <strong>{String(Boolean(header.OpenOrder))}</strong>
                </div>
                <div><span>Currency</span><strong>{header.CurrencyCode}</strong></div>
                <div><span>Total</span><strong>{fmtMoney(header.DocOrderAmt)}</strong></div>
                <div>
                  <span>ShipOrderComplete</span>
                  <strong>{String(Boolean(header.ShipOrderComplete))}</strong>
                </div>
                {header.OrderComment && (
                  <div><span>OrderComment</span><strong>{header.OrderComment}</strong></div>
                )}
                {header.ShipComment && (
                  <div><span>ShipComment</span><strong>{header.ShipComment}</strong></div>
                )}
                {Boolean(header.UseOTS) && (
                  <>
                    <div><span>UseOTS</span><strong>true</strong></div>
                    <div><span>OTS contact</span><strong>{header.OTSContact || '—'}</strong></div>
                    <div>
                      <span>OTS address</span>
                      <strong>
                        {[header.OTSAddress1, header.OTSAddress2, header.OTSCity, header.OTSState, header.OTSZIP]
                          .filter(Boolean).join(', ') || '—'}
                      </strong>
                    </div>
                    <div><span>OTS country</span><strong>{header.OTSCountryNum ?? '—'}</strong></div>
                  </>
                )}
              </div>
              {/* The check that was missing. A cleanup was recorded for six
                  orders and all six were still open; nothing compared the two
                  claims, so the ledger's word went unchallenged. */}
              {cleanupState?.stranded && (
                <div className="test-alert error">
                  {cleanupState.detail} Close it from the Cleanup card below.
                </div>
              )}
              {requestedShipVia && header.ShipViaCode !== requestedShipVia && (
                <div className="test-alert error">
                  This order asked for ShipViaCode {requestedShipVia} and Epicor
                  holds {header.ShipViaCode || '(blank)'}. A carrier default
                  overwrote the explicit choice, which means ShipViaCode was
                  written before the call that derives it rather than after — for a
                  collect order that is the difference between being picked up and
                  being shipped.
                </div>
              )}
              {!requestedShipVia && header.ShipViaCode && (
                <div className="test-alert notice">
                  No carrier was requested, so ShipViaCode {header.ShipViaCode} is
                  Epicor&apos;s own — derived by ChangeShipToID from ship-to
                  {' '}{header.ShipToNum || '(main)'}. That is the intended
                  behaviour for a delivery, not a discrepancy.
                </div>
              )}
              <table className="test-table">
                <thead>
                  <tr>
                    <th>Line</th><th>Part</th><th>Description</th>
                    <th>Warehouse</th>
                    <th className="num">Qty</th><th className="num">Unit</th>
                    <th className="num">Extended</th>
                  </tr>
                </thead>
                <tbody>
                  {(readback.order.lines || []).map((l) => (
                    <tr key={l.OrderLine}>
                      <td>{l.OrderLine}</td>
                      <td><code>{l.PartNum}</code></td>
                      <td>{l.LineDesc}</td>
                      <td>{l.WarehouseCode || '—'}</td>
                      <td className="num">{l.SellingQuantity ?? l.OrderQty}</td>
                      <td className="num">{fmtMoney(l.DocUnitPrice ?? l.UnitPrice)}</td>
                      <td className="num">{fmtMoney(l.DocExtPriceDtl ?? l.ExtPriceDtl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {readback && !readback.order && (
            <div className="test-alert notice">
              The order is no longer readable in Epicor
              {submission?.cleanup_action === 'deleted' ? ', which is expected: it was deleted.' : '.'}
            </div>
          )}
        </Card>
      )}

      {/* ── Cleanup ── */}
      {orderNum && (
        <Card
          title="Cleanup"
          desc={
            'Close and Void are the same call on this tenant. CloseOrder is what '
            + 'neutralises an order, and on an unshipped one Epicor implements it '
            + 'as a void: VoidOrder set, OpenOrder cleared, lines and releases '
            + 'voided, total zeroed. The two buttons differ in what they assert '
            + 'afterwards and what the ledger records, not in what they send. '
            + 'Deletion does not work here at all. Permission is not the same as '
            + 'a working cleanup: an order with allocated releases or a legal '
            + 'number may refuse, and that refusal is shown as Epicor worded it.'
          }
        >
          <div className="test-cleanup">
            <Button
              variant="secondary"
              disabled={Boolean(busyAction)}
              onClick={() => runCleanup('close')}
            >
              {busyAction === 'close' ? 'Closing…' : 'Close order'}
            </Button>
            <Button
              variant="ghost"
              disabled={Boolean(busyAction) || strandedLines.length > 0}
              onClick={() => runCleanup('reopen')}
              title={strandedLines.length > 0
                ? `Closing voided line ${strandedLines.join(', ')}, and ReopenOrder does not unvoid them. Reopening would leave a live order that cannot be shipped and is worth nothing.`
                : undefined}
            >
              {busyAction === 'reopen' ? 'Reopening…' : 'Reopen'}
            </Button>
            <Button
              variant="secondary"
              disabled={Boolean(busyAction)}
              onClick={() => runCleanup('void')}
            >
              {busyAction === 'void' ? 'Voiding…' : 'Void order (answers Q4)'}
            </Button>
          </div>
          {/* Not a hidden button: the reason it is unavailable is the finding. */}
          {strandedLines.length > 0 && (
            <div className="test-alert notice">
              Reopen is unavailable. Closing this order voided line
              {strandedLines.length > 1 ? 's' : ''} {strandedLines.join(', ')},
              and ReopenOrder restores the header without unvoiding them — it
              would leave a live order that cannot be shipped and is worth
              nothing. Raise a fresh order instead.
            </div>
          )}

          <div className="test-danger">
            <h4>Delete — not available on this tenant</h4>
            <p>
              <code>DeleteByID</code> answers HTTP 500 here for every real order,
              lined or empty. The permission probe in{' '}
              <code>probe_order_cleanup.md</code> returned &quot;Record not
              found&quot; against the sentinel <code>OrderNum 0</code>, which
              proved only that the method is reachable and passes security — not
              that it can delete anything. Void instead: it keeps the number and
              the audit trail, and drops the order out of credit exposure, which
              is the part that matters.
            </p>
            <p>
              The button below re-probes rather than cleans up. It exists so a
              tenant-side change can be detected, not because deletion is
              expected to work; a 500 is the normal answer and leaves the order
              untouched.
            </p>
            <div className="test-cleanup">
              <input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={String(orderNum)}
                aria-label="Type the order number to confirm deletion"
              />
              <Button
                variant="ghost"
                disabled={Boolean(busyAction) || deleteConfirm.trim() !== String(orderNum)}
                onClick={() => runCleanup('delete')}
              >
                {busyAction === 'delete' ? 'Trying…' : 'Retry DeleteByID'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ── Available stock beside a setup line ──
   The figure is the quote's own, not a separate stock read, so it is by
   construction the number the ready/backorder split was measured against.
   /inventory/stock could not agree: it filters neither side's is_present and
   does not expose the warehouse's, so no client-side filtering reproduces the
   quote's view of what is actually reachable. */
const SetupStock = ({ line, split }) => {
  if (!line.part) return <div className="wc-setup-stock" />;
  if (!split || split.available == null) {
    return <div className="wc-setup-stock is-muted">…</div>;
  }
  const pooled = split.basis === 'all enabled warehouses';
  // Tinted from the split rather than by comparing the figure to the quantity:
  // available_to_sell is the whole pool, so a second line for the same part
  // still reads the full number even after the first line has consumed most
  // of it. Only the split knows what was left by the time this line was
  // reached, and it also catches the partly-backordered case.
  const short = split.back > 0;
  return (
    <div
      className={`wc-setup-stock${short ? ' is-short' : ''}`}
      title={`${fmtQty(split.available)} available ${
        pooled ? 'across all enabled warehouses' : `at warehouse ${split.basis}`
      }`}
    >
      {fmtQty(split.available)} {pooled ? 'in stock' : `at ${split.basis}`}
    </div>
  );
};

/* ── One line in the summary or the backorder card ── */
const SummaryItem = ({ name, desc, qty, amount, backorder }) => (
  <div className="wc-item">
    <span className={`wc-item-thumb${backorder ? ' is-backorder' : ''}`}>
      {backorder ? <IconClock size={20} /> : <IconBox size={20} />}
    </span>
    <span className="wc-item-body">
      <span className="wc-item-name">{desc || name}</span>
      <span className="wc-item-qty">{name} · Qty {fmtQty(qty)}</span>
    </span>
    <span className="wc-item-price">{amount == null ? '—' : money(amount)}</span>
  </div>
);

/* ── The one-time address, as the storefront's "add a new address" ── */
const OneTimeAddress = ({ ots, setOts, countries, inAustralia }) => (
  <>
    <div className="wc-fields">
      <div>
        <span className="wc-label">First name</span>
        <input
          className="wc-input"
          value={ots.first_name}
          maxLength={50}
          onChange={(e) => setOts((p) => ({ ...p, first_name: e.target.value }))}
        />
      </div>
      <div>
        <span className="wc-label">Last name</span>
        <input
          className="wc-input"
          value={ots.last_name}
          maxLength={50}
          onChange={(e) => setOts((p) => ({ ...p, last_name: e.target.value }))}
        />
      </div>
    </div>
    <div className="wc-field" style={{ marginTop: 12 }}>
      <span className="wc-label">Address</span>
      <input
        className="wc-input"
        value={ots.address1}
        maxLength={50}
        onChange={(e) => setOts((p) => ({ ...p, address1: e.target.value }))}
      />
    </div>
    <div className="wc-field" style={{ marginTop: 12 }}>
      <span className="wc-label">Address line 2 (optional)</span>
      <input
        className="wc-input"
        value={ots.address2}
        maxLength={50}
        onChange={(e) => setOts((p) => ({ ...p, address2: e.target.value }))}
      />
    </div>
    <div className="wc-fields" style={{ marginTop: 12 }}>
      <div>
        <span className="wc-label">Suburb</span>
        <input
          className="wc-input"
          value={ots.suburb}
          maxLength={50}
          onChange={(e) => setOts((p) => ({ ...p, suburb: e.target.value }))}
        />
      </div>
      <div className="wc-narrow">
        <span className="wc-label">Postcode</span>
        <input
          className="wc-input"
          value={ots.postcode}
          maxLength={50}
          onChange={(e) => setOts((p) => ({ ...p, postcode: e.target.value }))}
        />
      </div>
    </div>
    <div className="wc-fields" style={{ marginTop: 12 }}>
      <div>
        <span className="wc-label">Country</span>
        <select
          className="wc-select"
          value={ots.country_num}
          onChange={(e) => setOts((p) => ({ ...p, country_num: e.target.value, state: '' }))}
        >
          <option value="">Select…</option>
          {countries.map((c) => (
            <option key={c.country_num} value={String(c.country_num)}>
              {c.description}
            </option>
          ))}
        </select>
        {countries.length === 0 && (
          <span className="wc-setup-warn">
            The country list could not be read from Epicor, so a one-time
            address cannot be submitted.
          </span>
        )}
      </div>
      <div>
        <span className="wc-label">State or Territory</span>
        {inAustralia ? (
          <select
            className="wc-select"
            value={ots.state}
            onChange={(e) => setOts((p) => ({ ...p, state: e.target.value }))}
          >
            <option value="">Select…</option>
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <input
            className="wc-input"
            value={ots.state}
            maxLength={50}
            onChange={(e) => setOts((p) => ({ ...p, state: e.target.value }))}
          />
        )}
      </div>
    </div>
    <p className="wc-section-note" style={{ marginTop: 10 }}>
      Posted to Epicor as a one-time ship-to (UseOTS) and not saved to the
      account.
    </p>
  </>
);

/* ── The order-placed screen ──
   Driven by the real submission and the quote the order was placed with, not
   a mock. The order number is Epicor's own. */
const PlacedOrder = ({ submission, quote, paymentLabel, pickup, onNewOrder }) => (
  <div className="wc-placed">
    <div className="wc-placed-head">
      <span className="wc-placed-tick"><IconCheck size={24} /></span>
      <div>
        <h2>Order placed</h2>
        <p className="wc-placed-sub">
          Order <code>{submission.epicor_order_num}</code> · a confirmation email
          is on its way.
        </p>
      </div>
    </div>

    <div className="wc-placed-stats">
      <div className="wc-stat">
        <div className="wc-stat-label">Charged now</div>
        <div className="wc-stat-value">{quote ? money(quote.pay_today) : '—'}</div>
        <div className="wc-stat-note">{paymentLabel}</div>
      </div>
      {quote?.has_backorder && (
        <div className="wc-stat">
          <div className="wc-stat-label is-muted">Backorder (charged later)</div>
          <div className="wc-stat-value is-muted">{money(quote.charged_later)}</div>
          <div className="wc-stat-note">
            Billed when the items are picked for shipping.
          </div>
        </div>
      )}
    </div>

    <div className="wc-next">
      <h3>What happens next</h3>
      <ol>
        <li>
          {pickup
            ? 'Your ready-to-collect items are picked today and held at your chosen branch.'
            : 'Your ready-to-ship items are picked today and dispatched on the delivery service you chose.'}
        </li>
        <li>
          {pickup
            ? 'We email you as soon as the order is available for collection.'
            : 'We email tracking as soon as the consignment leaves the warehouse.'}
        </li>
        {quote?.has_backorder && (
          <li>
            Backordered items are invoiced and charged separately once they are
            ready to ship.
          </li>
        )}
      </ol>
    </div>

    <div>
      <Button variant="secondary" onClick={onNewOrder}>Start a new order</Button>
    </div>
  </div>
);

/* ── Verification helpers ── */

const Question = ({ n, title, pass, detail, emphasis, children }) => (
  <div className={`test-question${emphasis ? ' emphasis' : ''}`}>
    <div className="test-question-head">
      <span className="test-question-n">{n}</span>
      <strong>{title}</strong>
      {pass === true && <Badge variant="success">pass</Badge>}
      {pass === false && <Badge variant="danger">fail</Badge>}
      {pass !== true && pass !== false && <Badge variant="neutral">unanswered</Badge>}
    </div>
    <p className="test-muted">{detail}</p>
    {children}
  </div>
);

const ParityTable = ({ header, lines }) => (
  <table className="test-table">
    <thead>
      <tr><th>Field</th><th>Ours</th><th>Reference</th><th>Match</th></tr>
    </thead>
    <tbody>
      {[...(header || []), ...(lines || [])].map((r, i) => (
        <tr key={`${r.field}-${i}`} className={r.match ? '' : 'mismatch'}>
          <td><code>{r.field}</code></td>
          <td>{String(r.ours ?? '—')}</td>
          <td>{String(r.reference ?? '—')}</td>
          <td>{r.match ? 'yes' : 'NO'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default TestCheckout;
